import { useEffect, useRef, useState } from 'react';
import { useWorkflowScope, workflowJson } from '../pages/mock/ContentApproval';
import { Button } from './ui';
import { toast } from './toast';
import type { StationCFigmaTemplate } from './stationcFigmaTemplates';
import { aiDraftFields } from './templateTextFit';

// =============================================================================
// AI 슬라이드 초안 요청 — 템플릿 갤러리의 AI 모드 오른쪽 위. 전달할 내용 하나로 고른 템플릿의 모든 문구를
// 채운다. 템플릿을 바꿔도 입력은 남아서 카드만 바꿔 가며 다시 생성할 수 있다. 문구만 만들고 저장·송출은 하지 않는다.
// =============================================================================

export type AiDraftResult = { values: Record<string, string>; missing: string[]; source: 'ai' | 'fallback'; message: string };
type CopyResponse = { source: 'ai' | 'fallback'; message: string; groupId: string | null; fields: Array<{ key: string; text: string }>; missing?: string[] };
const WRITERS = ['Owner', 'Admin', 'Editor'];

export function AiDraftPrompt({ template, groupId, generated, onDraft }: {
  template: StationCFigmaTemplate; groupId: string | null; generated: boolean;
  /** 요청한 템플릿 id 와 결과 — 기다리는 사이 다른 카드를 골라도 결과는 요청한 템플릿에 들어간다. */
  onDraft: (templateId: string, draft: AiDraftResult) => void;
}): JSX.Element {
  const work = useWorkflowScope();
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  const scope = work.scope, actor = scope?.actor;
  const writer = !!actor && WRITERS.includes(actor.role ?? '') && (!actor.enforced ||
    (groupId === null ? actor.central : scope!.groups.some((group) => group.id === groupId) && (actor.central ||
      actor.groups.some((grant) => grant.groupId === groupId && WRITERS.includes(grant.role)))));
  const scopeName = groupId ? scope?.groups.find((group) => group.id === groupId)?.name ?? '그룹 확인 중' : '공통';
  const ready = writer && !work.loading && !work.pending && !!prompt.trim();
  const generate = async () => {
    if (!ready || !work.begin()) return;
    setError(null);
    const requested = template, controller = new AbortController();
    request.current = controller;
    try {
      const copy = await workflowJson<CopyResponse>('assistant/notice-draft', { method: 'POST', signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, templateName: requested.name, guide: requested.ai?.guide, fields: aiDraftFields(requested), groupId }) });
      if (controller.signal.aborted) return;
      if (copy.groupId !== groupId) throw new Error('초안의 작성 범위를 다시 확인하세요.');
      onDraft(requested.id, { values: Object.fromEntries(copy.fields.map((field) => [field.key, field.text])),
        missing: copy.missing ?? [], source: copy.source, message: copy.message });
      // 타임아웃 등으로 폴백이면 화면 안 문구만으론 놓치기 쉽다 — 토스트로도 알린다.
      if (copy.source === 'fallback') toast(copy.message, 'error');
    } catch (reason) {
      if (controller.signal.aborted) return;
      const message = reason instanceof Error ? reason.message : String(reason);
      setError(message);
      toast(`AI 초안을 만들지 못했어요. ${message}`, 'error');
    }
    finally { work.finish(); }
  };
  return <section aria-label="AI 초안 요청" className="space-y-2 rounded-lg border border-line bg-panel-2 p-3">
    <div className="flex items-baseline justify-between gap-2 text-[13px]">
      <span className="font-semibold">전달할 내용</span>
      <span className="text-[12px] text-text-dim">작성 범위 · {scopeName}</span>
    </div>
    <textarea aria-label="AI 초안 요청 내용" rows={4} maxLength={2000} value={prompt} disabled={work.pending}
      placeholder={template.ai?.placeholder ?? '제목, 날짜, 장소, 대상, 문의처 등 담을 내용을 적어 주세요.'}
      className="w-full resize-y rounded border border-line bg-panel p-2 text-[13px] leading-relaxed"
      onChange={(event) => setPrompt(event.target.value)}
      onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void generate(); } }} />
    <div className="flex items-center justify-between gap-3">
      <p className="text-[12px] leading-snug text-text-dim">적지 않은 날짜·장소·문의처는 지어내지 않고 비워 둡니다.</p>
      <Button type="button" variant={generated ? 'default' : 'primary'} className="flex-none !py-1.5 !text-[14px]" disabled={!ready}
        title="⌘/Ctrl + Enter" onClick={() => void generate()}>
        {work.pending ? '초안 만드는 중…' : generated ? '다시 생성' : '초안 생성'}</Button>
    </div>
    {work.pending && <p role="status" className="text-[12px] text-text-dim">AI가 템플릿 칸에 맞춰 문구를 쓰고 있어요. 보통 5~15초 걸려요.</p>}
    {!writer && !work.loading && <p role="status" className="text-[12px] text-warning">현재 콘텐츠 그룹에 대한 편집 권한이 필요합니다.</p>}
    {(error || work.error) && <p role="alert" className="text-[12px] text-red">{error || work.error}</p>}
  </section>;
}
